import mapboxgl from 'mapbox-gl'
import { reactive } from 'vue'

// ── Fonds de carte additionnels (overlays) Géoplateforme IGN ─────────────────
// Tuiles WMTS de data.geopf.fr, servies en TileMatrixSet "PM" (Web Mercator) :
// directement compatibles avec le schéma XYZ d'une source `raster` Mapbox, sans
// proxy ni clé d'API (service public ouvert).
//
// Plusieurs overlays peuvent être empilés. L'ordre du tableau `active` = ordre
// d'affichage : index 0 = couche du dessus. Les overlais sont insérés AU-DESSUS
// du fond de carte de base mais EN DESSOUS des annotations / trace / drone, pour
// rester lisibles. L'état (couches actives + opacité) est persisté en
// localStorage et ré-appliqué après chaque changement de style de fond
// (`setStyle` efface toutes les sources/layers).

const GEOPF_WMTS = 'https://data.geopf.fr/wmts'
// Certaines couches (ex. carte OACI) ne sont pas servies par le WMTS public mais
// par l'endpoint « private » de la Géoplateforme, avec une clé d'accès ouverte
// dédiée au produit (pas de compte requis, service public).
const GEOPF_WMTS_OACI = 'https://data.geopf.fr/private/wmts?apikey=ign_scan_ws'
const ID_PREFIX = 'ign-overlay:'
const STORAGE_KEY = 'map-overlays-v1'

const IGN_ATTRIBUTION = 'IGN — Géoplateforme (data.geopf.fr)'

export interface OverlayDef {
  key: string // identifiant catalogue (et suffixe de l'id source/layer)
  name: string // libellé affiché
  description: string
  layer: string // identifiant de couche WMTS
  format: string // 'image/png' | 'image/jpeg'
  style: string // identifiant de style WMTS ('normal')
  minzoom: number
  maxzoom: number
  endpoint?: string // base WMTS si différente du WMTS public (ex. OACI → private)
}

export interface ActiveOverlay {
  key: string
  opacity: number // 0..1
  visible: boolean
}

// Catalogue — identifiants/format/zoom validés sur le GetCapabilities WMTS.
export const OVERLAY_CATALOG: OverlayDef[] = [
  {
    key: 'lidar-hd',
    name: 'LiDAR HD (ombrage)',
    description: 'Relief ombré issu du MNT LiDAR HD — très haute résolution (couverture en cours de déploiement).',
    layer: 'IGNF_LIDAR-HD_MNT_ELEVATION.ELEVATIONGRIDCOVERAGE.SHADOW',
    format: 'image/png',
    style: 'normal',
    minzoom: 0,
    maxzoom: 18,
  },
  {
    key: 'plan-ign',
    name: 'Plan IGN',
    description: 'Carte topographique vectorielle de l\'IGN (Plan IGN v2).',
    layer: 'GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2',
    format: 'image/png',
    style: 'normal',
    minzoom: 0,
    maxzoom: 19,
  },
  {
    key: 'ortho',
    name: 'Photos aériennes',
    description: 'Orthophotographies aériennes haute résolution (BD ORTHO).',
    layer: 'ORTHOIMAGERY.ORTHOPHOTOS',
    format: 'image/jpeg',
    style: 'normal',
    minzoom: 0,
    maxzoom: 19,
  },
  {
    key: 'oaci-vfr',
    name: 'Carte OACI (VFR)',
    description: 'Carte aéronautique OACI au 1:500 000 (vol à vue VFR) — données SIA/DGAC, mise à jour annuelle. Visible entre les zooms 6 et 11.',
    layer: 'GEOGRAPHICALGRIDSYSTEMS.MAPS.SCAN-OACI',
    format: 'image/jpeg',
    style: 'normal',
    minzoom: 6,
    maxzoom: 11,
    endpoint: GEOPF_WMTS_OACI,
  },
  {
    key: 'drone-loisir',
    name: 'Vol drone loisir',
    description: 'Restrictions d\'usage des drones de loisir (catégorie Ouverte) — zones interdites/limitées, transparent.',
    layer: 'TRANSPORTS.DRONES.RESTRICTIONS',
    format: 'image/png',
    style: 'normal',
    minzoom: 3,
    maxzoom: 13,
  },
  {
    key: 'cadastre',
    name: 'Parcelles cadastrales',
    description: 'Parcellaire express (PCI) — limites de parcelles, transparent.',
    layer: 'CADASTRALPARCELS.PARCELLAIRE_EXPRESS',
    format: 'image/png',
    style: 'normal',
    minzoom: 0,
    maxzoom: 19,
  },
  {
    key: 'contours',
    name: 'Courbes de niveau',
    description: 'Courbes de niveau (équidistance variable), transparent.',
    layer: 'ELEVATION.CONTOUR.LINE',
    format: 'image/png',
    style: 'normal',
    minzoom: 6,
    maxzoom: 18,
  },
  {
    key: 'slopes',
    name: 'Carte des pentes',
    description: 'Carte des pentes (≥ 30°…45°) — utile montagne / sécurité.',
    layer: 'ELEVATION.SLOPES',
    format: 'image/jpeg',
    style: 'normal',
    minzoom: 6,
    maxzoom: 14,
  },
]

export function getOverlayDef (key: string): OverlayDef | undefined {
  return OVERLAY_CATALOG.find(d => d.key === key)
}

function tileUrl (def: OverlayDef): string {
  const base = def.endpoint ?? GEOPF_WMTS
  // L'endpoint peut déjà porter une query (?apikey=…) : on enchaîne avec '&'.
  const sep = base.includes('?') ? '&' : '?'
  return `${base}${sep}SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0` +
    `&LAYER=${def.layer}&STYLE=${def.style}&FORMAT=${encodeURIComponent(def.format)}` +
    '&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}'
}

function srcLayerId (key: string): string {
  return ID_PREFIX + key
}

// Couches "au-dessus des overlays" : tout ce qui est ajouté par-dessus le fond
// (relief 3D, position, trace drone, annotations, outils de dessin). Les overlays
// s'insèrent juste avant la première de ces couches → ils recouvrent le fond mais
// pas les annotations. Les markers DOM (drone/RC) sont toujours au-dessus.
const ABOVE_OVERLAY_MATCHERS = [
  'sky', '3d-buildings', 'user-location-layer', 'drone-trail-layer', 'user-trail-layer',
  'polyline-layer-', 'polygon-', 'circle-', 'flightcircle-', 'flightpoly-', 'gl-draw',
]
function isAboveOverlay (id: string): boolean {
  return ABOVE_OVERLAY_MATCHERS.some(m => id === m || id.startsWith(m))
}

// ── État réactif partagé (singleton module) ─────────────────────────────────
function loadState (): ActiveOverlay[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as ActiveOverlay[]
    // On ne garde que les clés encore présentes au catalogue
    return parsed.filter(o => getOverlayDef(o.key))
  } catch (_) {
    return []
  }
}

const state = reactive<{ active: ActiveOverlay[] }>({ active: loadState() })

function persist () {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state.active))
  } catch (_) { /* quota / mode privé : on ignore */ }
}

let mapRef: mapboxgl.Map | null = null

function computeBeforeId (map: mapboxgl.Map): string | undefined {
  const layers = map.getStyle()?.layers || []
  return layers.find(l => isAboveOverlay(l.id))?.id
}

function ensureLayer (map: mapboxgl.Map, o: ActiveOverlay, beforeId?: string): void {
  const def = getOverlayDef(o.key)
  if (!def) return
  const id = srcLayerId(o.key)
  if (!map.getSource(id)) {
    map.addSource(id, {
      type: 'raster',
      tiles: [tileUrl(def)],
      tileSize: 256,
      minzoom: def.minzoom,
      maxzoom: def.maxzoom,
      attribution: IGN_ATTRIBUTION,
    } as mapboxgl.RasterSourceSpecification)
  }
  if (!map.getLayer(id)) {
    map.addLayer({
      id,
      type: 'raster',
      source: id,
      layout: { visibility: o.visible ? 'visible' : 'none' },
      paint: { 'raster-opacity': o.opacity },
    } as mapboxgl.RasterLayerSpecification, beforeId)
  }
}

// (Ré)applique l'ensemble des overlays actifs au style courant, dans le bon ordre.
function applyAll (): void {
  const map = mapRef
  if (!map || !map.isStyleLoaded()) return
  const beforeId = computeBeforeId(map)

  state.active.forEach(o => ensureLayer(map, o, beforeId))

  // Ordre relatif : index 0 = dessus. moveLayer(id, beforeId) place `id` juste
  // sous `beforeId`. En traitant du bas (dernier) vers le haut (index 0), la
  // dernière couche déplacée (index 0) finit au sommet du bloc d'overlays.
  for (let i = state.active.length - 1; i >= 0; i--) {
    const id = srcLayerId(state.active[i].key)
    if (map.getLayer(id)) map.moveLayer(id, beforeId)
  }

  // Synchronise opacité / visibilité
  state.active.forEach(o => {
    const id = srcLayerId(o.key)
    if (!map.getLayer(id)) return
    map.setPaintProperty(id, 'raster-opacity', o.opacity)
    map.setLayoutProperty(id, 'visibility', o.visible ? 'visible' : 'none')
  })
}

function removeLayerFromMap (key: string): void {
  const map = mapRef
  if (!map) return
  const id = srcLayerId(key)
  if (map.getLayer(id)) map.removeLayer(id)
  if (map.getSource(id)) map.removeSource(id)
}

export function useMapOverlays () {
  // À appeler une fois la carte chargée. Réinstalle les overlays sauvegardés et
  // les réapplique automatiquement après chaque changement de style de fond.
  function initOverlays (map: mapboxgl.Map): void {
    mapRef = map
    applyAll()
    map.on('style.load', () => applyAll())
  }

  function isActive (key: string): boolean {
    return state.active.some(o => o.key === key)
  }

  function addOverlay (key: string): void {
    if (!getOverlayDef(key) || isActive(key)) return
    // Ajouté en tête → s'affiche au-dessus des overlays déjà présents
    state.active.unshift({ key, opacity: 1, visible: true })
    persist()
    applyAll()
  }

  function removeOverlay (key: string): void {
    const idx = state.active.findIndex(o => o.key === key)
    if (idx === -1) return
    state.active.splice(idx, 1)
    persist()
    removeLayerFromMap(key)
  }

  function setOpacity (key: string, opacity: number): void {
    const o = state.active.find(o => o.key === key)
    if (!o) return
    o.opacity = Math.min(1, Math.max(0, opacity))
    persist()
    const map = mapRef
    if (map?.getLayer(srcLayerId(key))) {
      map.setPaintProperty(srcLayerId(key), 'raster-opacity', o.opacity)
    }
  }

  function toggleVisible (key: string): void {
    const o = state.active.find(o => o.key === key)
    if (!o) return
    o.visible = !o.visible
    persist()
    const map = mapRef
    if (map?.getLayer(srcLayerId(key))) {
      map.setLayoutProperty(srcLayerId(key), 'visibility', o.visible ? 'visible' : 'none')
    }
  }

  // Réordonne l'overlay de `from` vers `to` (indices dans state.active).
  function moveOverlay (from: number, to: number): void {
    if (from === to || from < 0 || to < 0 || from >= state.active.length || to >= state.active.length) return
    const [item] = state.active.splice(from, 1)
    state.active.splice(to, 0, item)
    persist()
    applyAll()
  }

  return {
    overlays: state,
    catalog: OVERLAY_CATALOG,
    initOverlays,
    isActive,
    addOverlay,
    removeOverlay,
    setOpacity,
    toggleVisible,
    moveOverlay,
    getOverlayDef,
  }
}
